import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';
import Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import { EntityPatch, PatchOperationType } from '@evidentid/rpweb-api-client';
import {
    ActionResolveInput,
    ActionReview,
    ActionReviewsQuery,
    ActionStatusFilter,
    ActionsTab,
    RevertImpact,
} from '@evidentid/tprm-portal-lib/models/entity-actions-review';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
import Tracker from '@evidentid/tracker';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { VuexCommonEntityStatus } from '@/models/VuexCommonEntityStatus.model';
import { VuexCommonListStatus } from '@/models/VuexCommonListStatus.model';
import { REVERT_PAUSE_FAILED_MESSAGE } from '@/modules/entity-actions-review/constants';
import { ActionsListFilters } from '@/modules/entity-actions-review/types';
import { getRevertErrorMessage } from '@/modules/entity-actions-review/utils/getRevertErrorMessage';
import {
    mutateCommonEntityFailState,
    mutateCommonEntityFinishState,
    mutateCommonEntityStartState,
} from '@/utils/vuex-common/commonEntityMutations';
import {
    mutateCommonListFailState,
    mutateCommonListFinishState,
    mutateCommonListStartState,
} from '@/utils/vuex-common/commonListMutations';
import {
    mutateCommonStatusFailState,
    mutateCommonStatusFinishState,
    mutateCommonStatusStartState,
} from '@/utils/vuex-common/commonStatusMutations';
import { performCommonCudAction } from '@/utils/vuex-common/performCommonCudAction';
import { performCommonGetAction } from '@/utils/vuex-common/performCommonGetAction';

export interface EntityActionsReviewRequirement {
    gatewayClient: GatewayApiClient;
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): EntityActionsReviewRequirement {
    return store.$app;
}

// The legacy module fired these through app.observer.onMutation() hooks in
// its module-builder registration; inlined at the same transition points.
function trackReviewEvent(
    app: EntityActionsReviewRequirement,
    event: { name: string, category: string, phase: string, subject: string },
    rpName: string,
): void {
    const { name, category, phase, subject } = event;
    const message = `[${phase}] ${subject} for ${rpName}`;
    app.tracker?.track({
        name: phase === 'Failed' ? `${name} Failed` : name,
        description: message,
        rpName,
    });
    app.logger?.addBreadcrumb({
        ...(phase === 'Failed' ? { level: Severity.error } : {}),
        category,
        message,
        data: { rpName },
    });
}

export interface CoiDocumentStatus {
    status: OperationStatus;
    list: any[];
}

export type CoiDocumentStatusPerAction = Record<string, CoiDocumentStatus>;

export interface EntityActionsListStatus {
    status: OperationStatus;
    list: ActionReview[];
    loading: boolean;
    finished: boolean;
    total: number | null;
    // Identifies the request currently loading (or last loaded) this tab. A response
    // whose caller-supplied key no longer matches this one belongs to a load the tab has
    // since moved past -- a sort, filter or tab change that started a new one before the
    // old one's response arrived -- and is dropped instead of being applied.
    loadKey: string | null;
}

export type EntityActionsByTab = Record<ActionsTab, EntityActionsListStatus>;

interface EntityActionsReviewState {
    entityActions: Record<string, EntityActionsByTab>;
    endorsementCategories: Record<string, VuexCommonListStatus<string>>;
    rprDocuments: Record<string, CoiDocumentStatusPerAction>;
    resolvingActionStatus: Record<string, OperationStatus>;
    resolveConflictError: Record<string, string | null>;
    actionCount: Record<string, VuexCommonEntityStatus<number>>;
    revertImpact: Record<string, VuexCommonEntityStatus<RevertImpact>>;
    revertingActionStatus: Record<string, OperationStatus>;
    revertActionError: Record<string, string | null>;
    entityDisplayNames: Record<string, string>;
}

const actionsReviewConfig = {
    recordsPerPage: 50,
};

// The Completed tab asks for `resolved`, not `completed`: a reverted action has a
// null completed_at but still belongs on that tab.
const tabStatusParam: Record<ActionsTab, ActionStatusFilter> = {
    [ActionsTab.pending]: ActionStatusFilter.pending,
    [ActionsTab.completed]: ActionStatusFilter.resolved,
};

/** Impact is per action, so its status map is keyed by both the RP and the action, the same
 * way rprDocuments is. */
export function revertImpactKey(rpName: string, actionId: string): string {
    return `${rpName}:${actionId}`;
}

export function entityDisplayNameKey(rpName: string, insuredId: string): string {
    return `${rpName}:${insuredId}`;
}

function createEmptyEntityActionsStatus(): EntityActionsListStatus {
    return {
        status: OperationStatus.uninitialized,
        list: [],
        loading: false,
        finished: false,
        total: null,
        loadKey: null,
    };
}

function createEmptyEntityActionsByTab(): EntityActionsByTab {
    return {
        [ActionsTab.pending]: createEmptyEntityActionsStatus(),
        [ActionsTab.completed]: createEmptyEntityActionsStatus(),
    };
}

interface EntityActionsHolder {
    entityActions: Record<string, EntityActionsByTab>;
}

function readTabStatus(holder: EntityActionsHolder, rpName: string, tab: ActionsTab): EntityActionsListStatus {
    return holder.entityActions[rpName]?.[tab] || createEmptyEntityActionsStatus();
}

function writeTabStatus(
    holder: EntityActionsHolder, rpName: string, tab: ActionsTab, next: EntityActionsListStatus,
): void {
    holder.entityActions = {
        ...holder.entityActions,
        [rpName]: {
            ...(holder.entityActions[rpName] || createEmptyEntityActionsByTab()),
            [tab]: next,
        },
    };
}

// A response is stale when the caller supplied a loadKey and it no longer matches the
// tab's currently tracked one -- a newer load already started for this tab, so applying
// this response would overwrite fresher data (or append onto a list it no longer belongs
// to) with results from a superseded request. A caller that omits loadKey entirely (every
// mutation call outside the load/loadNext actions themselves) is never treated as stale.
function isStaleLoad(current: EntityActionsListStatus, loadKey: string | null | undefined): boolean {
    return loadKey !== undefined && current.loadKey !== loadKey;
}

/** Builds the API query for one page of one tab; `entityId` is the portal's name for `insuredId`. */
function buildActionsQuery(
    tab: ActionsTab, filters: ActionsListFilters | undefined, sort: string | null, skip: number,
): ActionReviewsQuery {
    const { entityId, ...facets } = filters || {};
    return {
        skip,
        limit: actionsReviewConfig.recordsPerPage,
        sort: sort || null,
        status: tabStatusParam[tab],
        includeTotalValueInResponse: true,
        ...(entityId ? { insuredId: entityId } : {}),
        ...facets,
    };
}

export const useEntityActionsReviewStore = defineStore('entityActionsReview', {
    state: (): EntityActionsReviewState => ({
        entityActions: {},
        endorsementCategories: {},
        rprDocuments: {},
        resolvingActionStatus: {},
        resolveConflictError: {},
        actionCount: {},
        revertImpact: {},
        revertingActionStatus: {},
        revertActionError: {},
        entityDisplayNames: {},
    }),
    actions: {
        startLoadingEntityActions(payload: { rpName: string, tab: ActionsTab, loadKey?: string | null }) {
            trackReviewEvent(this.$app as EntityActionsReviewRequirement,
                { name: 'Load Entity Actions', category: 'Decisioning Criteria - Entity Actions', phase: 'Requested', subject: 'Load entity actions' },
                payload.rpName);
            const current = readTabStatus(this, payload.rpName, payload.tab);
            writeTabStatus(this, payload.rpName, payload.tab, {
                ...current,
                status: OperationStatus.loading,
                loading: true,
                loadKey: payload.loadKey === undefined ? current.loadKey : payload.loadKey,
            });
        },
        finishLoadingEntityActions(
            payload: {
                rpName: string; tab: ActionsTab; list: ActionReview[]; finished: boolean; total: number | null;
                loadKey?: string | null;
            },
        ) {
            const current = readTabStatus(this, payload.rpName, payload.tab);
            if (isStaleLoad(current, payload.loadKey)) {
                return;
            }
            trackReviewEvent(this.$app as EntityActionsReviewRequirement,
                { name: 'Load Entity Actions', category: 'Decisioning Criteria - Entity Actions', phase: 'Finished', subject: 'Load entity actions' },
                payload.rpName);
            writeTabStatus(this, payload.rpName, payload.tab, {
                status: OperationStatus.success,
                list: payload.list,
                loading: false,
                finished: payload.finished,
                total: payload.total,
                loadKey: payload.loadKey === undefined ? current.loadKey : payload.loadKey,
            });
        },
        appendEntityActions(
            payload: {
                rpName: string; tab: ActionsTab; list: ActionReview[]; finished: boolean; total: number | null;
                loadKey?: string | null;
            },
        ) {
            const current = readTabStatus(this, payload.rpName, payload.tab);
            if (isStaleLoad(current, payload.loadKey)) {
                return;
            }
            writeTabStatus(this, payload.rpName, payload.tab, {
                status: OperationStatus.success,
                list: [ ...current.list, ...payload.list ],
                loading: false,
                finished: payload.finished,
                total: payload.total,
                loadKey: payload.loadKey === undefined ? current.loadKey : payload.loadKey,
            });
        },
        failLoadingEntityActions(payload: { rpName: string, tab: ActionsTab, loadKey?: string | null }) {
            const current = readTabStatus(this, payload.rpName, payload.tab);
            if (isStaleLoad(current, payload.loadKey)) {
                return;
            }
            trackReviewEvent(this.$app as EntityActionsReviewRequirement,
                { name: 'Load Entity Actions', category: 'Decisioning Criteria - Entity Actions', phase: 'Failed', subject: 'Load entity actions' },
                payload.rpName);
            writeTabStatus(this, payload.rpName, payload.tab, {
                ...current,
                status: OperationStatus.error,
                loading: false,
            });
        },
        startLoadingEndorsementCategories(payload: { rpName: string }) {
            trackReviewEvent(this.$app as EntityActionsReviewRequirement,
                { name: 'Load Endorsement Categories', category: 'Decisioning Criteria - Endorsement Categories', phase: 'Requested', subject: 'Load endorsement categories' },
                payload.rpName);
            mutateCommonListStartState<string>(this, 'endorsementCategories', payload);
        },
        finishLoadingEndorsementCategories(payload: { rpName: string, list: string[] }) {
            trackReviewEvent(this.$app as EntityActionsReviewRequirement,
                { name: 'Load Endorsement Categories', category: 'Decisioning Criteria - Endorsement Categories', phase: 'Finished', subject: 'Load endorsement categories' },
                payload.rpName);
            mutateCommonListFinishState<string>(this, 'endorsementCategories', payload);
        },
        failLoadingEndorsementCategories(payload: { rpName: string }) {
            trackReviewEvent(this.$app as EntityActionsReviewRequirement,
                { name: 'Load Endorsement Categories', category: 'Decisioning Criteria - Endorsement Categories', phase: 'Failed', subject: 'Load endorsement categories' },
                payload.rpName);
            mutateCommonListFailState<string>(this, 'endorsementCategories', payload);
        },
        startLoadingRprDocuments(payload: { rpName: string, actionId: string }) {
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: {
                    ...(this.rprDocuments[payload.rpName] as CoiDocumentStatusPerAction),
                    [payload.actionId]: {
                        status: OperationStatus.loading,
                        list: [],
                    },
                },
            };
        },
        finishLoadingRprDocuments(
            payload: { rpName: string, actionId: string, documents: VerificationDocument[] },
        ) {
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: {
                    ...(this.rprDocuments[payload.rpName] as CoiDocumentStatusPerAction),
                    [payload.actionId]: {
                        status: OperationStatus.success,
                        list: payload.documents,
                    },
                },
            };
        },
        failLoadingRprDocuments(payload: { rpName: string, actionId: string }) {
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: {
                    ...(this.rprDocuments[payload.rpName] as CoiDocumentStatusPerAction),
                    [payload.actionId]: {
                        status: OperationStatus.error,
                        list: [],
                    },
                },
            };
        },
        startResolvingAction(payload: { rpName: string }) {
            mutateCommonStatusStartState(this, 'resolvingActionStatus', payload);
        },
        finishResolvingAction(payload: { rpName: string, tab: ActionsTab, actionId: string }) {
            // Custom logic for removing the resolved action from the list
            const current = readTabStatus(this, payload.rpName, payload.tab);
            const actionList = current.list || [];
            const actionIndex = actionList.findIndex((action) => action.id === payload.actionId);
            if (actionIndex >= 0) {
                writeTabStatus(this, payload.rpName, payload.tab, {
                    ...current,
                    status: OperationStatus.success,
                    list: [ ...actionList.slice(0, actionIndex), ...actionList.slice(actionIndex + 1) ],
                });
            }
            mutateCommonStatusFinishState(this, 'resolvingActionStatus', payload);
        },
        failResolvingAction(payload: { rpName: string }) {
            mutateCommonStatusFailState(this, 'resolvingActionStatus', payload);
        },

        startLoadingActionCount(payload: { rpName: string }) {
            mutateCommonEntityStartState<number>(this, 'actionCount', payload);
        },
        finishLoadingActionCount(payload: { rpName: string, data: number }) {
            mutateCommonEntityFinishState<number>(this, 'actionCount', payload);
        },
        failLoadingActionCount(payload: { rpName: string }) {
            mutateCommonEntityFailState<number>(this, 'actionCount', payload);
        },
        decrementActionCount(payload: { rpName: string }) {
            const currentCount = this.actionCount[payload.rpName]?.data;
            if (typeof currentCount === 'number') {
                mutateCommonEntityFinishState<number>(this, 'actionCount', {
                    rpName: payload.rpName,
                    data: Math.max(0, currentCount - 1),
                });
            }
        },

        startLoadingRevertImpact(payload: { rpName: string, actionId: string }) {
            mutateCommonEntityStartState<RevertImpact>(this, 'revertImpact', {
                rpName: revertImpactKey(payload.rpName, payload.actionId),
            });
        },
        finishLoadingRevertImpact(payload: { rpName: string, actionId: string, data: RevertImpact }) {
            mutateCommonEntityFinishState<RevertImpact>(this, 'revertImpact', {
                rpName: revertImpactKey(payload.rpName, payload.actionId),
                data: payload.data,
            });
        },
        failLoadingRevertImpact(payload: { rpName: string, actionId: string }) {
            mutateCommonEntityFailState<RevertImpact>(this, 'revertImpact', {
                rpName: revertImpactKey(payload.rpName, payload.actionId),
            });
        },
        clearRevertImpact(payload: { rpName: string, actionId: string }) {
            this.revertImpact = omit(this.revertImpact, [ revertImpactKey(payload.rpName, payload.actionId) ]);
        },

        startRevertingAction(payload: { rpName: string }) {
            mutateCommonStatusStartState(this, 'revertingActionStatus', payload);
        },
        // Replaces the reverted row in place on the Completed tab instead of removing it: a
        // reverted action still has a resolution to look back on, so it stays on that tab with
        // its status flipped, rather than disappearing the way a resolved pending action does.
        finishRevertingAction(payload: { rpName: string, actionId: string, action: ActionReview }) {
            const perTab = this.entityActions[payload.rpName];
            const completed = perTab?.[ActionsTab.completed];
            const index = completed
                ? completed.list.findIndex((action) => action.id === payload.actionId)
                : -1;
            if (completed && index >= 0) {
                this.entityActions = {
                    ...this.entityActions,
                    [payload.rpName]: {
                        ...perTab,
                        [ActionsTab.completed]: {
                            ...completed,
                            list: [
                                ...completed.list.slice(0, index),
                                payload.action,
                                ...completed.list.slice(index + 1),
                            ],
                        },
                    },
                };
            }
            mutateCommonStatusFinishState(this, 'revertingActionStatus', payload);
        },
        failRevertingAction(payload: { rpName: string }) {
            mutateCommonStatusFailState(this, 'revertingActionStatus', payload);
        },
        setRevertActionError(payload: { rpName: string, error: string | null }) {
            this.revertActionError = {
                ...this.revertActionError,
                [payload.rpName]: payload.error,
            };
        },
        clearRevertingActionStatus(payload: { rpName: string }) {
            this.revertingActionStatus = omit(this.revertingActionStatus, [ payload.rpName ]);
            this.revertActionError = omit(this.revertActionError, [ payload.rpName ]);
        },

        clearEntityActionsStatus(rpName: string) {
            this.entityActions = omit(this.entityActions, [ rpName ]) as Record<string, EntityActionsByTab>;
        },
        // Resets one tab back to its empty state without touching the sibling tab, unlike
        // clearEntityActionsStatus which drops the whole rpName entry.
        clearEntityActionsTabStatus(payload: { rpName: string, tab: ActionsTab }) {
            writeTabStatus(this, payload.rpName, payload.tab, createEmptyEntityActionsStatus());
        },
        clearEndorsementCategoriesStatus(rpName: string) {
            this.endorsementCategories = omit(this.endorsementCategories, [ rpName ]);
        },
        clearEndorsementPerAction(payload: { rpName: string, actionId: string }) {
            const { rpName, actionId } = payload;
            this.rprDocuments = {
                ...this.rprDocuments,
                [rpName]: {
                    ...omit(this.rprDocuments[rpName], actionId),
                },
            };
        },
        setResolveConflictError(payload: { rpName: string, error: string | null }) {
            this.resolveConflictError = {
                ...this.resolveConflictError,
                [payload.rpName]: payload.error,
            };
        },

        async loadEntityActions(payload: {
            rpName: string;
            tab: ActionsTab;
            sort?: string | null;
            filters?: ActionsListFilters;
            // Identifies the caller's current load intent (its rpName/tab/sort/filters, and
            // anything else the caller considers part of "what changed"). Carried through to
            // the mutations below so a response that arrives after a newer load has started
            // for this tab is dropped instead of overwriting or appending onto fresher data.
            loadKey?: string | null;
        }) {
            const { rpName, tab, sort, loadKey } = payload;
            const currentStatus = readTabStatus(this, rpName, tab);

            // Prevent concurrent loading of the same tab; the other tab loads independently.
            if (currentStatus.loading) {
                return;
            }

            this.startLoadingEntityActions({ rpName, tab, loadKey });

            const queryOptions = buildActionsQuery(tab, payload.filters, sort || null, 0);

            try {
                const response = await appOf(this).gatewayClient.tprmUi()
                    .getInsuranceInsuredActions(rpName, queryOptions);
                const actions = response.actions;
                const finished = actions.length < actionsReviewConfig.recordsPerPage;
                this.finishLoadingEntityActions({
                    rpName, tab, list: actions, finished, total: response.total, loadKey,
                });
            } catch (error) {
                // for now the insuredId should be the only query that we should handle differently when invalid format,
                // if we need to support more in the future we should change this check to be less explicit
                if (error.reason === 'bad-request' &&
                    error.xhrObj.responseText.includes('Invalid value for: query parameter insuredId')) {
                    // The empty list is the whole handling. Rethrowing would travel up the awaiting
                    // watcher into Vue's error handler and replace the app with the error page, hiding
                    // the very list written here.
                    this.finishLoadingEntityActions({
                        rpName,
                        tab,
                        list: [],
                        finished: true,
                        total: 0,
                        loadKey,
                    });
                } else {
                    this.failLoadingEntityActions({ rpName, tab, loadKey });
                    await getPersistingErrorActions().showError(error);
                }
            }
        },
        async loadNextEntityActions(payload: {
            rpName: string;
            tab: ActionsTab;
            sort?: string | null;
            filters?: ActionsListFilters;
            loadKey?: string | null;
        }) {
            const { rpName, tab, sort, loadKey } = payload;
            const currentStatus = readTabStatus(this, rpName, tab);

            // Prevent concurrent loading or loading when finished
            if (currentStatus.loading || currentStatus.finished) {
                return;
            }

            this.startLoadingEntityActions({ rpName, tab, loadKey });

            const queryOptions = buildActionsQuery(tab, payload.filters, sort || null, currentStatus.list.length);

            try {
                const response = await appOf(this).gatewayClient.tprmUi()
                    .getInsuranceInsuredActions(rpName, queryOptions);
                const actions = response.actions;
                const finished = actions.length < actionsReviewConfig.recordsPerPage;
                this.appendEntityActions({ rpName, tab, list: actions, finished, total: response.total, loadKey });
            } catch (error) {
                this.failLoadingEntityActions({ rpName, tab, loadKey });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadEndorsementCategories(payload: { rpName: string }) {
            const { rpName } = payload;
            const mutateStart = () => this.startLoadingEndorsementCategories({ rpName });
            const mutateFinish = (list: string[]) => this.finishLoadingEndorsementCategories({ rpName, list });
            const mutateFail = () => this.failLoadingEndorsementCategories({ rpName });
            const getDataCall = () => appOf(this).gatewayClient.tprmUi().getInsuranceEndorsementCategories();
            await performCommonGetAction<string>(
                { state: this }, 'endorsementCategories', rpName, getDataCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        async resolveEntityAction(payload: {
            rpName: string; tab: ActionsTab; actionId: string; resolve: ActionResolveInput;
        }) {
            const { rpName, tab, actionId, resolve } = payload;
            const mutateStart = () => {
                this.startResolvingAction({ rpName });
                this.setResolveConflictError({ rpName, error: null });
            };
            const mutateFinish = () => {
                this.finishResolvingAction({ rpName, tab, actionId });
                this.decrementActionCount({ rpName });
            };
            const mutateFail = async (error: any) => {
                if (error?.error === 'Conflict' || error?.error === 'KeyConflict') {
                    this.failResolvingAction({ rpName });
                    this.setResolveConflictError({ rpName, error: error.error });
                } else {
                    console.warn('Resolve action error');
                    this.failResolvingAction({ rpName });
                    await getPersistingErrorActions().showError(error);
                }
            };
            const actionCall = () => appOf(this).gatewayClient.tprmUi().createActionResolve(rpName, actionId, resolve);
            await performCommonCudAction(
                { state: this },
                'resolvingActionStatus', rpName, actionCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        async loadRprDocuments(payload: {
            rpName: string;
            actionId: string;
            documentsFor: {
                verificationId: string;
                requirementType: TprmRequirementType;
                instanceId?: string | null;
            }[];
        }) {
            const { rpName, actionId, documentsFor } = payload;
            const mutateStart = () => this.startLoadingRprDocuments({ rpName, actionId });
            const mutateFinish = (documents: VerificationDocument[]) =>
                this.finishLoadingRprDocuments({ rpName, actionId, documents });
            const mutateFail = () => {
                this.failLoadingRprDocuments({ rpName, actionId });
                console.warn(`No certificate of documents found for ${actionId}`);
            };

            const getDataCall = () => Promise.all(
                documentsFor.map(({ verificationId, requirementType, instanceId }) => appOf(this).gatewayClient
                    .submissionTprmUi()
                    .getDocuments({
                        cn: rpName,
                        verId: verificationId,
                        requirement: requirementType,
                        instanceId: instanceId || undefined,
                    })),
            ).then((responses) => responses.flatMap((response: any) => response.documents || []));
            // We use a custom state key that combines rpName and actionId
            const stateKey = `${rpName}:${actionId}`;
            await performCommonGetAction<VerificationDocument>(
                { state: this },
                'rprDocuments', stateKey, getDataCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        async loadActionCount(payload: { rpName: string }) {
            const { rpName } = payload;
            const mutateStart = () => this.startLoadingActionCount({ rpName });
            const mutateFinish = (data: number) => this.finishLoadingActionCount({ rpName, data });
            const mutateFail = () => this.failLoadingActionCount({ rpName });
            const getDataCall = () => appOf(this).gatewayClient.tprmUi().getActionsCount(rpName);
            await performCommonGetAction<number>(
                { state: this },
                'actionCount', rpName, getDataCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        // Does not go through performCommonGetAction: that helper's eighth parameter is
        // isList, not a skip flag, and its catch calls getPersistingErrorActions().showError
        // unconditionally with no way to opt out. Routing a failed impact check through it
        // would replace the whole application with the error page, when the failure needs to
        // land in revertImpact[key].status instead, for the modal's own impactUnavailable
        // state to show.
        async loadRevertImpact(payload: { rpName: string, actionId: string }) {
            const { rpName, actionId } = payload;
            const key = revertImpactKey(rpName, actionId);
            // Read through a function rather than the property access directly: TypeScript's
            // control flow analysis otherwise narrows the type of the property access itself
            // and carries that narrowing across the calls and the await below, even though
            // those calls are exactly what changes the value being read.
            const readStatus = () => this.revertImpact[key]?.status ?? OperationStatus.uninitialized;
            if (readStatus() === OperationStatus.loading) {
                return;
            }
            this.startLoadingRevertImpact({ rpName, actionId });
            try {
                const data = await appOf(this).gatewayClient.tprmUi().getActionRevertImpact(rpName, actionId);
                const latestStatus = readStatus();
                // Handle race condition: a newer call already moved this key past loading.
                if (latestStatus !== OperationStatus.loading && latestStatus !== OperationStatus.uninitialized) {
                    return;
                }
                this.finishLoadingRevertImpact({ rpName, actionId, data });
            } catch {
                this.failLoadingRevertImpact({ rpName, actionId });
            }
        },
        setEntityDisplayName(payload: { key: string, name: string }) {
            this.entityDisplayNames = { ...this.entityDisplayNames, [payload.key]: payload.name };
        },
        // Bypasses performCommonGetAction, whose failure path replaces the page with the error screen. A missing
        // name only changes the wording of the document callout.
        async loadEntityDisplayName(payload: { rpName: string, insuredId: string }) {
            const { rpName, insuredId } = payload;
            const key = entityDisplayNameKey(rpName, insuredId);
            if (this.entityDisplayNames[key] !== undefined) {
                return;
            }
            try {
                const entity = await appOf(this).gatewayClient.tprmUi().getTprmEntity(rpName, insuredId);
                this.setEntityDisplayName({ key, name: entity.displayName });
            } catch {
                // The callout falls back to wording without the entity name.
            }
        },

        /**
         * Pauses one entity ahead of reverting its action, so re-evaluation does not send it reminders.
         *
         * Returns whether the pause took effect; the caller abandons the revert when it did not.
         * `patchTprmEntity` reports a rejected patch in its result rather than throwing, so a failure
         * has to be read off the counts.
         */
        async pauseEntity(payload: { rpName: string, insuredId: string }): Promise<boolean> {
            const { rpName, insuredId } = payload;
            this.setRevertActionError({ rpName, error: null });
            try {
                const { failureCount } = await appOf(this).gatewayClient.tprmUi().patchTprmEntity(rpName, [ {
                    id: insuredId,
                    paused: { op: PatchOperationType.replace, newValue: true },
                } as EntityPatch ]);
                if (failureCount > 0) {
                    this.setRevertActionError({ rpName, error: REVERT_PAUSE_FAILED_MESSAGE });
                    return false;
                }
                return true;
            } catch {
                this.setRevertActionError({ rpName, error: REVERT_PAUSE_FAILED_MESSAGE });
                return false;
            }
        },
        async revertAction(payload: { rpName: string, actionId: string, reason?: string }) {
            const { rpName, actionId, reason } = payload;
            const mutateStart = () => {
                this.startRevertingAction({ rpName });
                this.setRevertActionError({ rpName, error: null });
            };
            const mutateFinish = (action: ActionReview) =>
                this.finishRevertingAction({ rpName, actionId, action });
            // The pending action count is left untouched here: a reverted action is not
            // re-linked to the queue until the backend's fan-out runs, which has not
            // happened yet when this response returns. Incrementing now would count an
            // action that is not yet pending, and double-count once a later load picks it up.
            const mutateFail = async (error: any) => {
                this.failRevertingAction({ rpName });
                const message = getRevertErrorMessage(error);
                if (message) {
                    this.setRevertActionError({ rpName, error: message });
                } else {
                    await getPersistingErrorActions().showError(error);
                }
            };
            const actionCall = () => appOf(this).gatewayClient.tprmUi()
                .revertAction(rpName, actionId, reason);
            await performCommonCudAction(
                { state: this },
                'revertingActionStatus', rpName, actionCall, mutateStart, mutateFinish, mutateFail, true,
            );
        },
        clearEndorsementPerRp(payload: { rpName: string }) {
            this.rprDocuments = omit(this.rprDocuments, [ payload.rpName ]);
        },
        clearEntityActions(payload: { rpName: string }) {
            this.clearEntityActionsStatus(payload.rpName);
        },
        // Used before a single tab reloads, so the other tab's already-loaded list is
        // left in place instead of being wiped along with it.
        clearEntityActionsTab(payload: { rpName: string, tab: ActionsTab }) {
            this.clearEntityActionsTabStatus(payload);
        },
        clearEndorsementCategories(payload: { rpName: string }) {
            this.clearEndorsementCategoriesStatus(payload.rpName);
        },
        clearResolvingEntityActionStatus(payload: { rpName: string }) {
            this.resolvingActionStatus = omit(this.resolvingActionStatus, [ payload.rpName ]);
        },
        finishResolvedAction(payload: { rpName: string, tab: ActionsTab, actionId: string }) {
            this.finishResolvingAction({ rpName: payload.rpName, tab: payload.tab, actionId: payload.actionId });
            this.decrementActionCount({ rpName: payload.rpName });
        },
        clearActionCount(payload: { rpName: string }) {
            this.actionCount = omit(this.actionCount, [ payload.rpName ]);
        },
    },
});
